import React, { useState, CSSProperties } from 'react';
import Select, {
  components,
  ActionMeta,
  OptionProps,
  IndicatorProps,
  SingleValueProps,
} from 'react-select';
import styled, { keyframes } from 'styled-components';
import { BreakpointsOfTOrT, breakpoint, theme } from '../../theme';
import {
  CaretIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SvgComponent,
} from '../../assets';
import { Alignment, VerticalAlignment } from '../../types';
import { PrimaryButton, SecondaryButton, Button, ButtonSize } from '../button';
import {
  optionStyle,
  controlStyle,
  indicatorSeparatorStyle,
  dropdownIndicatorStyle,
  menuStyle,
  menuPortalStyle,
  singleValueStyle,
  valueContainerStyle,
  placeholderStyle,
} from './styles';

export type DropDownProps = {
  align?: Alignment;
  verticalAlign?: VerticalAlignment;
  inline?: boolean;
  disabled?: boolean;
  label?: string;
  onChange: (option: string) => void;
  onClick?: (e: Event) => void;
  onBlur?: (e: Event) => void;
  onFocus?: (e: Event) => void;
  options: DropDownOption[];
  maxWidth?: string;
  fullWidth?: boolean;
  dropDownButtonType?: 'default' | 'primary' | 'secondary' | 'large' | 'button';
  buttonSize?: BreakpointsOfTOrT<ButtonSize>;
  selectedValue: string;
  overrideOption?: DropDownOption;
  placeholder?: string | JSX.Element;
  color?: string;
  topMargin?: string;
  menuPortalTarget?: HTMLElement | null;
  hideChevronRightIcon?: boolean;
};

export type DropDownOption = {
  label: string;
  value: string;
  icon?: SvgComponent;
};

export const animationDuration = 200;

const menuOpenKeyframes = keyframes`
  0% {
    max-height: 0;
  }
  100% {
    max-height: 500px;
  }
`;

export const MenuComponent = styled(components.Menu)`
  animation: ${animationDuration}ms ${menuOpenKeyframes} ease-in;
`;

const Wrapper = styled.div<{ inline?: boolean }>`
  position: relative;
  display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')};
  align-items: center;
`;

const Content = styled.div`
  position: relative;
  transition: height linear 0.4s;
  width: 100%;
`;

const MenuWrapper = styled.div<{ align: Alignment }>`
  display: flex;
  justify-content: ${({ align }) =>
    `${align === 'left' ? 'flex-start' : 'flex-end'}`};
  z-index: 2;
  width: 100%;
`;

const Label = styled.label`
  ${theme.typography.uiText.md}
  margin: 0 ${theme.spacings[1].rem} 0 0;
  display: flex;
  align-items: center;
`;

const Icon = styled(CaretIcon)<{ color: string; open?: boolean }>`
  fill: ${({ color }) => color};
  height: 18px;
  width: 14px;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  transition: transform ${animationDuration}ms ease-in-out;
  vertical-align: middle;
`;

const ButtonIcon = styled(ChevronDownIcon)<{
  disabled: boolean;
  open?: boolean;
}>`
  height: 18px;
  width: 14px;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  transition: transform ${animationDuration}ms ease-in-out;
`;

const RightIcon = styled(ChevronRightIcon)`
  margin-left: auto;
  color: ${theme.colors.primary.graysGray};
`;

const StyledSelect = styled(Select)`
  display: inline-block;
`;

const StyledLargeOption = styled(components.Option)`
  font-family: ${theme.fonts.roboto};
  ${theme.typography.uiText.md};
  width: 204px;
  height: 56px;
  align-items: center;
  display: flex;
  padding: ${theme.spacings[4].rem};
  gap: ${theme.spacings[3].rem};
  ${breakpoint('sm')`
    width: 236px;
  `}
`;

const StyledButton = styled(Button)`
  background-color: ${theme.colors.neutrals.solidGrays[7]};
`;

type SelectStylesProps = {
  disabled: boolean;
  align: Alignment;
  isAnimateOpen: boolean;
  topMargin: string;
  verticalAlign?: VerticalAlignment;
  maxWidth?: string;
  fullWidth?: boolean;
};

const selectStyles = ({
  disabled,
  align = 'left',
  isAnimateOpen,
  topMargin,
  verticalAlign,
  maxWidth,
  fullWidth,
}: SelectStylesProps) => ({
  option: (
    _: CSSProperties,
    { isFocused }: OptionProps<Record<string, unknown>, boolean>
  ) => ({
    ...optionStyle,
    cursor: 'pointer',
    backgroundColor: isFocused ? theme.colors.neutrals.solidGrays[8] : 'unset',
  }),
  control: () => ({
    ...controlStyle,
  }),
  indicatorSeparator: () => ({
    ...indicatorSeparatorStyle,
  }),
  indicatorsContainer: () => ({}),
  dropdownIndicator: () => ({
    ...dropdownIndicatorStyle,
    marginLeft: '4px',
  }),
  menu: () => ({
    ...menuStyle,
    ...(align === 'left' ? { left: 0 } : { right: 0 }),
    ...(verticalAlign === 'top'
      ? { top: 0 }
      : verticalAlign === 'bottom'
      ? { bottom: 0 }
      : {}),
    minWidth: 'fit-content',
    ...(maxWidth && { maxWidth }),
    borderBottom: 'none',
    borderRadius: 0,
    margin: `${topMargin} 0 0 0`,
    maxHeight: isAnimateOpen ? '500px' : '0',
    transition: `max-height ${animationDuration}ms ease-in-out`,
  }),
  menuPortal: () => ({
    ...menuPortalStyle,
  }),
  container: (provided: CSSProperties) => ({
    ...provided,
    width: '100%',
  }),
  menuList: (provided: CSSProperties) => ({
    ...provided,
    padding: 0,
    maxHeight: '500px',
  }),
  singleValue: () => ({
    ...singleValueStyle,
    color: disabled
      ? theme.colors.neutrals.solidGrays[3]
      : theme.colors.system.systemBlue,
    ...(align === 'left' ? { marginRight: 'auto' } : { marginLeft: 'auto' }),
    ...(fullWidth && { width: '100%', overflow: 'visible' }),
  }),
  valueContainer: () => ({
    ...valueContainerStyle,
    padding: 0,
    ...(fullWidth && { width: '100%', overflow: 'visible' }),
  }),
  placeholder: () => ({
    ...placeholderStyle,
    color: `${theme.colors.primary.graysGray}`,
  }),
});

const DropdownIndicator = (props: IndicatorProps<any, false>) => {
  const { color } = props.selectProps;
  return (
    <components.DropdownIndicator {...props}>
      <Icon open={props.selectProps.menuIsOpen} color={color} />
    </components.DropdownIndicator>
  );
};

const Menu = (props: any) => (
  <MenuComponent {...props}>{props.children}</MenuComponent>
);

const ButtonOption = (props: any) => {
  return (
    <components.Option {...props}>
      {typeof props.data.icon === 'undefined' ? null : (
        <props.data.icon width={15} />
      )}{' '}
      {props.data.label}
    </components.Option>
  );
};

const LargeOption = (props: any, hideChevronRightIcon?: boolean) => {
  return (
    <StyledLargeOption {...props}>
      {typeof props.data.icon === 'undefined' ? null : (
        <props.data.icon width={24} />
      )}
      {props.data.label}
      {hideChevronRightIcon ? null : <RightIcon width={18} />}
    </StyledLargeOption>
  );
};

const SingleValue = (props: SingleValueProps<any>) => {
  const { dropDownButtonType, buttonSize, isFocussed } = props.selectProps;
  return (
    <components.SingleValue {...props}>
      {dropDownButtonType === 'primary' ? (
        <PrimaryButton
          fullWidth
          size={buttonSize}
          isActive={props.selectProps.menuIsOpen}
        >
          {props.children}
          <ButtonIcon
            disabled={props.isDisabled}
            open={props.selectProps.menuIsOpen}
          />
        </PrimaryButton>
      ) : dropDownButtonType === 'button' ? (
        <StyledButton
          fullWidth
          size={buttonSize}
          applyFocusStyle={isFocussed}
          isFocusControlled={true}
        >
          {props.children}
          <ButtonIcon
            disabled={props.isDisabled}
            open={props.selectProps.menuIsOpen}
          />
        </StyledButton>
      ) : (
        <SecondaryButton
          fullWidth
          size={buttonSize}
          isActive={props.selectProps.menuIsOpen}
        >
          {props.children}
          <ButtonIcon
            disabled={props.isDisabled}
            open={props.selectProps.menuIsOpen}
          />
        </SecondaryButton>
      )}
    </components.SingleValue>
  );
};

export const DropDown: React.FC<DropDownProps> = ({
  align = 'left',
  hideChevronRightIcon = false,
  verticalAlign,
  inline = false,
  disabled = false,
  color = theme.colors.system.systemBlue,
  label,
  onChange,
  onClick,
  onBlur,
  onFocus,
  options,
  selectedValue,
  maxWidth,
  fullWidth,
  dropDownButtonType = 'default',
  buttonSize,
  overrideOption,
  placeholder,
  topMargin = theme.spacings[2].rem,
  menuPortalTarget,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimateOpen, setIsAnimateOpen] = useState(false);
  const [isFocussed, setIsFocussed] = useState(false);

  const handleChange = (
    selectedOption: DropDownOption,
    { action }: ActionMeta<DropDownOption>
  ) => {
    if (action === 'select-option') {
      onChange(selectedOption.value);
    }
  };

  const openMenu = () => {
    if (!isOpen) {
      setIsOpen(true);
      setIsAnimateOpen(true);
    }
  };

  const closeMenu = () => {
    if (isOpen) {
      setIsAnimateOpen(false);
      setTimeout(() => {
        setIsOpen(false);
      }, animationDuration);
    }
  };

  return (
    <Wrapper inline={inline} {...props}>
      <Content>
        <MenuWrapper align={align}>
          {label && <Label>{label}:</Label>}
          <StyledSelect
            color={disabled ? theme.colors.neutrals.solidGrays[3] : color}
            menuIsOpen={isOpen}
            onMenuClose={closeMenu}
            onMenuOpen={openMenu}
            isDisabled={disabled}
            components={{
              Menu,
              DropdownIndicator,
              ...(dropDownButtonType === 'primary' ||
              dropDownButtonType === 'secondary' ||
              dropDownButtonType === 'button'
                ? {
                    SingleValue,
                    Option: ButtonOption,
                    DropdownIndicator: () => null,
                  }
                : dropDownButtonType === 'large' && { Option: LargeOption }),
            }}
            isSearchable={false}
            onBlur={() => setIsFocussed(false)}
            onFocus={() => setIsFocussed(true)}
            onChange={handleChange}
            onClick={onClick}
            options={options}
            styles={selectStyles({
              disabled,
              align,
              verticalAlign,
              isAnimateOpen,
              topMargin,
              maxWidth,
              fullWidth,
            })}
            value={
              overrideOption ||
              options.filter((option) => option.value === selectedValue)
            }
            buttonSize={buttonSize}
            dropDownButtonType={dropDownButtonType}
            isFocussed={isFocussed}
            placeholder={placeholder}
            menuPortalTarget={menuPortalTarget}
          />
        </MenuWrapper>
      </Content>
    </Wrapper>
  );
};
